import React, { useState, useEffect } from 'react';
import './Admin.css';

const API_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_SOCKET_URL || '';

function getBrowserDate() {
  const now = new Date();
  const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return localDate.toISOString().split('T')[0];
}

function utcToLocal(timestamp) {
  const date = new Date(timestamp);
  return new Date(date.getTime()).toLocaleString();
}

function formatUserFriendlyDate(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}

export function Admin() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authHeader, setAuthHeader] = useState('');

  // State management
  const [activeView, setActiveView] = useState('events'); // 'events' or 'catalog'
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventPrizes, setEventPrizes] = useState([]);
  const [prizeCatalog, setPrizeCatalog] = useState([]);
  const [awardedPrizes, setAwardedPrizes] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showArchivedPrizes, setShowArchivedPrizes] = useState(false);
  
  // Modal states
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreatePrize, setShowCreatePrize] = useState(false);
  const [showDuplicateEvent, setShowDuplicateEvent] = useState(false);
  const [showAddPrizeToEvent, setShowAddPrizeToEvent] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [showDeleteEvent, setShowDeleteEvent] = useState(false);
  const [deleteEventData, setDeleteEventData] = useState(null);
  
  // Form states
  const [newEventDate, setNewEventDate] = useState('');
  const [newPrize, setNewPrize] = useState({ name: '', image: '', description: '' });
  const [duplicateSource, setDuplicateSource] = useState('');
  const [duplicateTarget, setDuplicateTarget] = useState('');
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [addPrizeQuantities, setAddPrizeQuantities] = useState({});
  
  // Notifications
  const [notification, setNotification] = useState(null);
  
  // Timezone management
  const [currentTimezone, setCurrentTimezone] = useState('');
  const [eventCurrentDate, setEventCurrentDate] = useState('');
  const [availableTimezones, setAvailableTimezones] = useState([]);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);

  const getCurrentDate = () => eventCurrentDate || getBrowserDate();
  
  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  // Authentication function
  const handleAuthentication = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');

    try {
      // First test the password with the auth endpoint
      const response = await fetch(`${API_BASE}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        // Create Basic Auth header for future API calls
        const basicAuthHeader = `Basic ${btoa(`admin:${password}`)}`;
        setAuthHeader(basicAuthHeader);
        setIsAuthenticated(true);
        setPassword('');
      } else {
        const data = await response.json();
        setAuthError(data.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setAuthError('Connection error. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
      fetchPrizeCatalog(showArchivedPrizes);
      fetchTimezone();
    }
  }, [showArchivedPrizes, isAuthenticated]);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventData(selectedEvent);
    }
  }, [selectedEvent]);

  // Auto-select today's event if available
  useEffect(() => {
    if (events.length > 0 && !selectedEvent && eventCurrentDate) {
      const today = getCurrentDate();
      const todayEvent = events.find(e => e === today);
      setSelectedEvent(todayEvent || events[0]);
    }
  }, [events, selectedEvent, eventCurrentDate]);

  // Calculate next available date for new events
  const getNextAvailableDate = () => {
    if (events.length === 0) {
      return getCurrentDate();
    }
    
    // Find the latest date and add 1 day
    const sortedDates = [...events].sort();
    const latestDate = sortedDates[sortedDates.length - 1];
    const nextDate = new Date(latestDate);
    nextDate.setDate(nextDate.getDate() + 1);
    
    // Format as YYYY-MM-DD
    return nextDate.toISOString().split('T')[0];
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`, {
        headers: {
          'Authorization': authHeader
        }
      });
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
      showNotification('Error fetching events', 'error');
    }
  };

  const fetchPrizeCatalog = async (includeArchived = false) => {
    try {
      const url = includeArchived 
        ? `${API_BASE}/api/prizes/catalog?includeArchived=true`
        : `${API_BASE}/api/prizes/catalog`;
      const response = await fetch(url, {
        headers: {
          'Authorization': authHeader
        }
      });
      const data = await response.json();
      setPrizeCatalog(data);
    } catch (error) {
      console.error('Error fetching prize catalog:', error);
      showNotification('Error fetching prize catalog', 'error');
    }
  };

  const fetchEventData = async (date) => {
    try {
      const [eventResponse, awardedResponse] = await Promise.all([
        fetch(`${API_BASE}/api/events/${date}`, {
          headers: { 'Authorization': authHeader }
        }),
        fetch(`${API_BASE}/api/awardedprizes/${date}`, {
          headers: { 'Authorization': authHeader }
        })
      ]);
      
      const eventData = await eventResponse.json();
      const awardedData = await awardedResponse.json();
      
      setEventPrizes(eventData);
      setAwardedPrizes(awardedData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching event data:', error);
      setEventPrizes([]);
      setAwardedPrizes([]);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchTimezone = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/timezone`, {
        headers: {
          'Authorization': authHeader
        }
      });
      const data = await response.json();
      setCurrentTimezone(data.timezone);
      setEventCurrentDate(data.currentDate || '');
      setAvailableTimezones(data.availableTimezones);
    } catch (error) {
      console.error('Error fetching timezone:', error);
    }
  };

  const updateTimezone = async (timezone) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/timezone`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ timezone })
      });
      
      if (!response.ok) throw new Error('Failed to update timezone');
      
      const data = await response.json();
      setCurrentTimezone(data.timezone);
      setEventCurrentDate(data.currentDate || '');
      setShowTimezoneModal(false);
      showNotification('Timezone updated successfully');
      
      fetchEvents();
      if (data.currentDate) {
        setSelectedEvent(data.currentDate);
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
      showNotification('Error updating timezone', 'error');
    }
  };

  const downloadCSV = (date) => {
    const url = `${API_BASE}/api/awardedprizes/${date}?csv=true`;
    window.open(url, '_blank');
  };

  const handleImageUpload = async (file, prizeId = null) => {
    if (!file) return null;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return null;
    }
    
    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      showNotification('Image must be smaller than 2MB', 'error');
      return null;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (prizeId) formData.append('prizeId', prizeId);
      
      const response = await fetch(`${API_BASE}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setUploadingImage(false);
      showNotification('Image uploaded successfully');
      return data.imagePath;
    } catch (error) {
      setUploadingImage(false);
      console.error('Error uploading image:', error);
      showNotification('Error uploading image', 'error');
      return null;
    }
  };

  const addPrizeToEvent = async (prizeId, quantity) => {
    try {
      const response = await fetch(`${API_BASE}/api/events/${selectedEvent}/prizes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          prizeId: parseInt(prizeId),
          quantity: quantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add prize to event');
      }

      setShowAddPrizeToEvent(false);
      setAddPrizeQuantities({});
      fetchEventData(selectedEvent);
      showNotification('Prize updated for event successfully');
    } catch (error) {
      console.error('Error adding prize to event:', error);
      showNotification(error.message, 'error');
    }
  };

  // Prize Catalog Management
  const createPrize = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/prizes/catalog`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(newPrize)
      });
      
      if (!response.ok) throw new Error('Failed to create prize');
      
      setNewPrize({ name: '', image: '', description: '' });
      setShowCreatePrize(false);
      fetchPrizeCatalog(showArchivedPrizes);
      showNotification('Prize created successfully');
    } catch (error) {
      console.error('Error creating prize:', error);
      showNotification('Error creating prize', 'error');
    }
  };

  const updatePrize = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/prizes/catalog/${editingPrize.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(editingPrize)
      });
      
      if (!response.ok) throw new Error('Failed to update prize');
      
      setEditingPrize(null);
      fetchPrizeCatalog(showArchivedPrizes);
      showNotification('Prize updated successfully');
    } catch (error) {
      console.error('Error updating prize:', error);
      showNotification('Error updating prize', 'error');
    }
  };

  const archivePrize = async (prizeId) => {
    try {
      const response = await fetch(`${API_BASE}/api/prizes/catalog/${prizeId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader
        }
      });
      
      if (!response.ok) throw new Error('Failed to archive prize');
      
      fetchPrizeCatalog(showArchivedPrizes);
      showNotification('Prize archived successfully');
    } catch (error) {
      console.error('Error archiving prize:', error);
      showNotification('Error archiving prize', 'error');
    }
  };

  const unarchivePrize = async (prizeId) => {
    try {
      const response = await fetch(`${API_BASE}/api/prizes/catalog/${prizeId}/unarchive`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader
        }
      });
      
      if (!response.ok) throw new Error('Failed to unarchive prize');
      
      fetchPrizeCatalog(showArchivedPrizes);
      showNotification('Prize unarchived successfully');
    } catch (error) {
      console.error('Error unarchiving prize:', error);
      showNotification('Error unarchiving prize', 'error');
    }
  };

  // Event Management
  const createEvent = async () => {
    try {
      // Create empty event - no prizes needed
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          date: newEventDate,
          prizes: []
        })
      });

      if (!response.ok) throw new Error('Failed to create event');

      setShowCreateEvent(false);
      setNewEventDate('');
      fetchEvents();
      showNotification('Event created successfully. Add prizes to this event to make them available for the wheel.');
    } catch (error) {
      console.error('Error creating event:', error);
      showNotification('Error creating event', 'error');
    }
  };

  const duplicateEvent = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events/${duplicateSource}/duplicate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ targetDate: duplicateTarget, override: duplicateOverride })
      });
      
      if (response.status === 409) {
        const data = await response.json();
        if (data.requiresOverride) {
          showNotification('Target date already has an event. Check "Override Existing Prizes" to replace it.', 'error');
          return;
        }
      }

      if (!response.ok) throw new Error('Failed to duplicate event');

      setShowDuplicateEvent(false);
      setDuplicateSource('');
      setDuplicateTarget('');
      setDuplicateOverride(false);
      fetchEvents();
      showNotification('Event duplicated successfully');
    } catch (error) {
      console.error('Error duplicating event:', error);
      showNotification('Error duplicating event', 'error');
    }
  };

  // Event Deletion
  const initiateDeleteEvent = async (date) => {
    try {
      const response = await fetch(`${API_BASE}/api/events/${date}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          showNotification('Event not found', 'error');
          return;
        }
        throw new Error('Failed to check event');
      }

      const data = await response.json();
      
      if (data.hasAwardedPrizes) {
        // Show confirmation modal with awarded prizes info
        setDeleteEventData(data);
        setShowDeleteEvent(true);
      } else {
        // Event was already deleted (no awarded prizes)
        fetchEvents();
        if (selectedEvent === date) {
          setSelectedEvent(null);
        }
        showNotification('Event deleted successfully');
      }
    } catch (error) {
      console.error('Error checking event for deletion:', error);
      showNotification('Error checking event', 'error');
    }
  };

  const confirmDeleteEvent = async () => {
    if (!deleteEventData) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/events/${deleteEventData.event.date}/force`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader
        }
      });

      if (!response.ok) throw new Error('Failed to delete event');

      setShowDeleteEvent(false);
      setDeleteEventData(null);
      fetchEvents();
      
      if (selectedEvent === deleteEventData.event.date) {
        setSelectedEvent(null);
      }
      
      showNotification('Event and all associated data deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      showNotification('Error deleting event', 'error');
    }
  };

  const updatePrizeQuantity = async (prizeItem, newQuantity) => {
    // Update local state immediately
    setEventPrizes(prevPrizes => 
      prevPrizes.map(prize => 
        prize.id === prizeItem.id 
          ? { ...prize, quantity: newQuantity, available: newQuantity - prize.awarded } 
          : prize
      )
    );

    try {
      const response = await fetch(`${API_BASE}/api/updatePrizeQuantity`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          availablePrizeId: prizeItem.id,
          quantity: newQuantity
        })
      });

      if (!response.ok) throw new Error('Failed to update quantity');
    } catch (error) {
      console.error('Error updating prize quantity:', error);
      // Revert local state on error
      setEventPrizes(prevPrizes => 
        prevPrizes.map(prize => 
          prize.id === prizeItem.id 
            ? { ...prize, quantity: prizeItem.quantity, available: prizeItem.quantity - prize.awarded } 
            : prize
        )
      );
      showNotification('Error updating prize quantity', 'error');
    }
  };

  const getEventStatus = (date) => {
    const today = getCurrentDate();
    if (date === today) return 'active';
    if (date < today) return 'completed';
    return 'upcoming';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'completed': return '#6c757d';
      case 'upcoming': return '#007bff';
      default: return '#6c757d';
    }
  };

  // Show authentication form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="auth-overlay">
          <div className="auth-modal">
            <div className="auth-header">
              <h2>Admin Access</h2>
              <p>Please enter the admin password to continue</p>
            </div>
            <form onSubmit={handleAuthentication} className="auth-form">
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>
              {authError && (
                <div className="auth-error">
                  {authError}
                </div>
              )}
              <button 
                type="submit" 
                className="btn-primary auth-submit"
                disabled={isAuthenticating || !password.trim()}
              >
                {isAuthenticating ? 'Authenticating...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* Navbar Header */}
      <nav className="admin-navbar">
        <div className="navbar-brand">
          <h1>Prize Management</h1>
        </div>
        <div className="navbar-nav">
          <button 
            className={`nav-btn ${activeView === 'events' ? 'active' : ''}`}
            onClick={() => setActiveView('events')}
          >
            Events
          </button>
          <button 
            className={`nav-btn ${activeView === 'catalog' ? 'active' : ''}`}
            onClick={() => setActiveView('catalog')}
          >
            Prizes
          </button>
        </div>
        <div className="navbar-info">
          <button 
            className="timezone-btn"
            onClick={() => setShowTimezoneModal(true)}
            title="Change Timezone"
          >
            🌍 {currentTimezone || 'Loading...'}
            {eventCurrentDate && ` · ${eventCurrentDate}`}
            {eventCurrentDate && ` · ${eventCurrentDate}`}
          </button>
          <span>Last updated: {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}</span>
        </div>
      </nav>

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
          <button onClick={() => setNotification(null)}>×</button>
        </div>
      )}

      <div className="admin-layout">
        {/* Sidebar */}
        <div className="admin-sidebar">
          {activeView === 'events' ? (
            <>
              <div className="sidebar-header">
                <h3>Events</h3>
                <div className="sidebar-actions">
                                      <button 
                      className="btn-icon" 
                      onClick={() => {
                        setNewEventDate(getNextAvailableDate());
                        setShowCreateEvent(true);
                      }}
                      title="Create New Event"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    </button>
                    <button 
                      className="btn-icon" 
                      onClick={() => {
                        setDuplicateTarget(getNextAvailableDate());
                        setShowDuplicateEvent(true);
                      }}
                      title="Duplicate Event"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"/>
                      </svg>
                    </button>
                </div>
              </div>
              
              <div className="event-list">
                {events.length === 0 ? (
                  <div className="empty-events">
                    <p>No events created yet.</p>
                    <p>Click ➕ to create your first event!</p>
                  </div>
                ) : (
                  events.map((date) => {
                    const status = getEventStatus(date);
                    return (
                      <div
                        key={date}
                        className={`event-item ${selectedEvent === date ? 'selected' : ''}`}
                        onClick={() => setSelectedEvent(date)}
                      >
                        <div className="event-date" title={date}>
                        {formatUserFriendlyDate(date)}
                      </div>
                        <div 
                          className="event-status"
                          style={{ backgroundColor: getStatusColor(status) }}
                        >
                          {status}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className="sidebar-header">
                <h3>Prize Catalog</h3>
                <button 
                  className="btn-icon" 
                  onClick={() => setShowCreatePrize(true)}
                  title="Create New Prize"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </button>
              </div>
              
              <div className="prize-list">
                {prizeCatalog.length === 0 ? (
                  <div className="empty-prizes">
                    <p>{showArchivedPrizes ? 'No archived prizes.' : 'No prizes in catalog yet.'}</p>
                    {!showArchivedPrizes && <p>Click ➕ to add your first prize!</p>}
                  </div>
                ) : (
                  prizeCatalog.map((prize) => (
                    <div
                      key={prize.id}
                      className={`prize-item ${prize.isArchived ? 'archived' : ''}`}
                      onClick={() => setEditingPrize({ ...prize })}
                    >
                      <img 
                        src={prize.image} 
                        alt={prize.name} 
                        className="prize-thumb"
                      />
                      <div className="prize-info">
                        <div className="prize-name">{prize.name}</div>
                        <div className="prize-usage">
                          Used in {prize.availablePrizes?.length || 0} events
                        </div>
                        {prize.isArchived && (
                          <div className="archive-status">Archived</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className="admin-main">
          {activeView === 'events' && selectedEvent ? (
            <>
              {/* Event Prizes */}
              <div className="content-section">
                <div className="section-header">
                  <h2>Prizes for {formatUserFriendlyDate(selectedEvent)}</h2>
                                      <button 
                      className="btn-primary"
                      onClick={() => {
                        setNewEventDate(getNextAvailableDate());
                        setShowAddPrizeToEvent(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px'}}>
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                      Add Prize to Event
                    </button>
                    <button 
                      className="btn-danger"
                      onClick={() => initiateDeleteEvent(selectedEvent)}
                      title="Delete This Event"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px'}}>
                        <polyline points="3,6 5,6 21,6"/>
                        <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                      Delete Event
                    </button>
                </div>
                {eventPrizes.length === 0 ? (
                  <div className="empty-prizes-message">
                    <div className="empty-icon">🎁</div>
                    <h3>No Prizes Added Yet</h3>
                    <p>This event doesn't have any prizes. Click "Add Prize to Event" to get started.</p>
                  </div>
                ) : (
                  <div className="prize-grid">
                    {eventPrizes.map((prize) => (
                      <div key={prize.id} className={`prize-card ${prize.available === 0 ? 'zero-available' : ''}`}>
                      <img 
                        src={prize.image} 
                        alt={prize.name} 
                        className="prize-image"
                      />
                      <div className="prize-details">
                        <h3>{prize.name}</h3>
                        <p>{prize.description}</p>
                        <div className="prize-controls">
                          <div className="quantity-control">
                            <label>Quantity:</label>
                            <input
                              type="number"
                              value={prize.quantity}
                              min="0"
                              onChange={(e) => updatePrizeQuantity(prize, parseInt(e.target.value) || 0)}
                            />
                          </div>
                          <div className="prize-stats">
                            <div>Awarded: {prize.awarded}</div>
                            <div>Available: {prize.available}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                )}
              </div>

              {/* Winner History */}
              <div className="content-section">
                <div className="section-header">
                  <h2>Winner History for {formatUserFriendlyDate(selectedEvent)}</h2>
                                      <button 
                      className="btn-outline"
                      onClick={() => downloadCSV(selectedEvent)}
                      disabled={awardedPrizes.length === 0}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px'}}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download CSV
                    </button>
                </div>
                <div className="winner-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Prize Name</th>
                        <th>Timestamp</th>
                        <th>Subscribe Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {awardedPrizes.map((winner) => (
                        <tr key={winner.id}>
                          <td>{winner.email}</td>
                          <td>{winner.prizeName}</td>
                          <td>{utcToLocal(winner.timestamp)}</td>
                          <td>
                            {winner.subscribeStatus || '—'}
                            {winner.subscribeStatus === 'failed' && winner.subscribeError && (
                              <span title={winner.subscribeError} style={{ marginLeft: '6px', color: '#dc2626', cursor: 'help' }}>⚠</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : activeView === 'catalog' ? (
            <div className="content-section">
              <div className="section-header">
                <div>
                  <h2>Prize Catalog Management</h2>
                  <p className="section-description">
                    Manage your prize catalog. Create new prizes or edit existing ones. Prizes can be reused across multiple events.
                  </p>
                </div>
                <div className="archive-toggle">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showArchivedPrizes}
                      onChange={() => setShowArchivedPrizes(!showArchivedPrizes)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">Show Archived</span>
                  </label>
                </div>
              </div>
              
              <div className="catalog-grid">
                {prizeCatalog.map((prize) => (
                  <div key={prize.id} className={`catalog-card ${prize.isArchived ? 'archived' : ''}`}>
                    <img 
                      src={prize.image} 
                      alt={prize.name} 
                      className="catalog-image"
                    />
                    <h3>{prize.name}</h3>
                    <p>{prize.description}</p>
                    <div className="catalog-usage">
                      Used in {prize.availablePrizes?.length || 0} events
                      {prize.isArchived && <span className="archive-badge">ARCHIVED</span>}
                    </div>
                    <div className="catalog-actions">
                      <button 
                        className="btn-outline"
                        onClick={() => setEditingPrize({ ...prize })}
                      >
                        Edit
                      </button>
                      {prize.isArchived ? (
                        <button 
                          className="btn-success"
                          onClick={() => unarchivePrize(prize.id)}
                        >
                          Unarchive
                        </button>
                      ) : (
                        <button 
                          className="btn-danger"
                          onClick={() => archivePrize(prize.id)}
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="content-section">
              <div className="empty-state">
                <h3>Select an event from the sidebar</h3>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateEvent && (
        <div className="modal-overlay" onClick={() => setShowCreateEvent(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Event</h3>
              <button onClick={() => setShowCreateEvent(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Event Date:</label>
                <input
                  type="date"
                  value={newEventDate}
                  onChange={(e) => setNewEventDate(e.target.value)}
                />
              </div>
              
              <p className="help-text">
                Create an empty event for this date. You can add prizes to the event after creation.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowCreateEvent(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createEvent}>
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Prize Modal */}
      {showCreatePrize && (
        <div className="modal-overlay" onClick={() => setShowCreatePrize(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Prize</h3>
              <button onClick={() => setShowCreatePrize(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Prize Name:</label>
                <input
                  type="text"
                  value={newPrize.name}
                  onChange={(e) => setNewPrize(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Prize Image:</label>
                <p className="upload-instructions">
                  Images should be a minimum of 512x512 PNG files with transparent backgrounds. Maximum file size of 2MB.
                </p>
                <div className="image-upload-section">
                  <div className="current-image">
                    {newPrize.image && (
                      <img 
                        src={newPrize.image} 
                        alt="Prize preview" 
                        className="preview-image"
                      />
                    )}
                  </div>
                  <div className="image-upload-controls">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const imagePath = await handleImageUpload(file);
                          if (imagePath) {
                            setNewPrize(prev => ({ ...prev, image: imagePath }));
                          }
                        }
                      }}
                      style={{ display: 'none' }}
                      id="new-image-upload"
                    />
                    <label htmlFor="new-image-upload" className="btn-outline">
                      {uploadingImage ? 'Uploading...' : 'Choose Image'}
                    </label>
                    <input
                      type="text"
                      value={newPrize.image}
                      onChange={(e) => setNewPrize(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="Or enter image path manually"
                      className="manual-path-input"
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={newPrize.description}
                  onChange={(e) => setNewPrize(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the prize"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowCreatePrize(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createPrize}>
                Create Prize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Prize Modal */}
      {editingPrize && (
        <div className="modal-overlay" onClick={() => setEditingPrize(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Prize</h3>
              <button onClick={() => setEditingPrize(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Prize Name:</label>
                <input
                  type="text"
                  value={editingPrize.name}
                  onChange={(e) => setEditingPrize(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Prize Image:</label>
                <p className="upload-instructions">
                  Images should be a minimum of 512x512 PNG files with transparent backgrounds. Maximum file size of 2MB.
                </p>
                <div className="image-upload-section">
                  <div className="current-image">
                    {editingPrize.image && (
                      <img 
                        src={editingPrize.image} 
                        alt="Current prize image" 
                        className="preview-image"
                      />
                    )}
                  </div>
                  <div className="image-upload-controls">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const imagePath = await handleImageUpload(file, editingPrize.id);
                          if (imagePath) {
                            setEditingPrize(prev => ({ ...prev, image: imagePath }));
                          }
                        }
                      }}
                      style={{ display: 'none' }}
                      id="edit-image-upload"
                    />
                    <label htmlFor="edit-image-upload" className="btn-outline">
                      {uploadingImage ? 'Uploading...' : 'Choose New Image'}
                    </label>
                    <input
                      type="text"
                      value={editingPrize.image}
                      onChange={(e) => setEditingPrize(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="Or enter image path manually"
                      className="manual-path-input"
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={editingPrize.description || ''}
                  onChange={(e) => setEditingPrize(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setEditingPrize(null)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={updatePrize}>
                Update Prize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Event Modal */}
      {showDuplicateEvent && (
        <div className="modal-overlay" onClick={() => setShowDuplicateEvent(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Event</h3>
              <button onClick={() => setShowDuplicateEvent(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Copy all prizes and quantities from one event to a new date.</p>
              
              <div className="form-group">
                <label>Source Event Date:</label>
                <select
                  value={duplicateSource}
                  onChange={(e) => setDuplicateSource(e.target.value)}
                >
                  <option value="">Select source date</option>
                  {events.map(date => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Target Event Date:</label>
                <input
                  type="date"
                  value={duplicateTarget}
                  onChange={(e) => setDuplicateTarget(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Override Existing Prizes:</label>
                <input
                  type="checkbox"
                  checked={duplicateOverride}
                  onChange={(e) => setDuplicateOverride(e.target.checked)}
                />
                <span>If checked, existing prizes on the target date will be replaced.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowDuplicateEvent(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={duplicateEvent}>
                Duplicate Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Event Confirmation Modal */}
      {showDeleteEvent && deleteEventData && (
        <div className="modal-overlay" onClick={() => setShowDeleteEvent(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Delete Event Confirmation</h3>
              <button onClick={() => setShowDeleteEvent(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="warning-message">
                <p><strong>You are about to delete the event for {formatUserFriendlyDate(deleteEventData.event.date)}.</strong></p>
                
                {deleteEventData.hasAwardedPrizes ? (
                  <div className="awarded-prizes-warning">
                    <h4>⚠️ This event has awarded prizes!</h4>
                    <p>Total prizes awarded: <strong>{deleteEventData.totalAwarded}</strong></p>
                    
                    <div className="awarded-prizes-list">
                      <h5>Awarded Prizes Details:</h5>
                      {deleteEventData.awardedPrizes.map((prize, index) => (
                        <div key={index} className="awarded-prize-item">
                          <strong>{prize.prizeName}</strong>: {prize.awarded} awarded
                          <div className="winners-list">
                            {prize.winners.slice(0, 3).map((winner, winnerIndex) => (
                              <div key={winnerIndex} className="winner-item">
                                {winner.email} - {new Date(winner.timestamp).toLocaleString()}
                              </div>
                            ))}
                            {prize.winners.length > 3 && (
                              <div className="more-winners">... and {prize.winners.length - 3} more</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <p className="delete-warning">
                      <strong>⚠️ Warning:</strong> Deleting this event will permanently remove all prize assignments 
                      and winner records. Consider downloading the winner data first for your records.
                    </p>
                  </div>
                ) : (
                  <p>This event has no awarded prizes and can be safely deleted.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowDeleteEvent(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmDeleteEvent}>
                {deleteEventData.hasAwardedPrizes ? 'Delete Event & All Data' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Prize to Event Modal */}
      {showAddPrizeToEvent && (
        <div className="modal-overlay" onClick={() => setShowAddPrizeToEvent(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Prize to {formatUserFriendlyDate(selectedEvent)}</h3>
              <button onClick={() => setShowAddPrizeToEvent(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="help-text">
                Select a prize from your catalog to add to this event.
              </p>
              
                             {prizeCatalog
                 .filter(prize => !prize.isArchived && !eventPrizes.some(ep => ep.prizeId === prize.id))
                 .map((prize) => (
                   <div key={prize.id} className="add-prize-item">
                     <img src={prize.image} alt={prize.name} />
                     <div className="prize-info">
                       <div className="prize-name">{prize.name}</div>
                       <div className="prize-description">{prize.description}</div>
                     </div>
                     <div className="add-prize-controls">
                       <div className="quantity-input-wrapper">
                         <label>Qty:</label>
                         <input
                           type="number"
                           min="1"
                           value={addPrizeQuantities[prize.id] || 1}
                           onChange={(e) => setAddPrizeQuantities(prev => ({ 
                             ...prev, 
                             [prize.id]: parseInt(e.target.value) || 1 
                           }))}
                         />
                       </div>
                       <button 
                         className="btn-primary"
                         onClick={() => {
                           const quantity = addPrizeQuantities[prize.id] || 1;
                           addPrizeToEvent(prize.id, quantity);
                         }}
                       >
                         Add
                       </button>
                     </div>
                   </div>
                 ))}
              
              {prizeCatalog.filter(prize => !prize.isArchived && !eventPrizes.some(ep => ep.prizeId === prize.id)).length === 0 && (
                <p className="no-prizes">All active prizes from your catalog are already in this event.</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowAddPrizeToEvent(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timezone Modal */}
      {showTimezoneModal && (
        <div className="modal-overlay" onClick={() => setShowTimezoneModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Event Timezone</h3>
              <button onClick={() => setShowTimezoneModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="help-text">
                Select the timezone for your events. This affects when the day changes for prize allocation.
              </p>
              
              <div className="form-group">
                <label>Current Timezone:</label>
                <div className="current-timezone">
                  🌍 {currentTimezone}
                </div>
              </div>

              <div className="form-group">
                <label>Current Event Date:</label>
                <div className="current-timezone">
                  {eventCurrentDate ? formatUserFriendlyDate(eventCurrentDate) : 'Loading...'}
                </div>
              </div>
              
              <div className="form-group">
                <label>Select New Timezone:</label>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateTimezone(e.target.value);
                    }
                  }}
                  defaultValue=""
                >
                  <option value="">Choose a timezone...</option>
                  {availableTimezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowTimezoneModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
